import dayjs, { Dayjs } from 'dayjs';
import locale from 'dayjs/locale/de';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { ITimeDistribution } from '../interfaces/StoreTypes';
import { IProfile, ITime, IWorkingdays } from '../interfaces/Types';
import DayjsUtils from './DayjsUtils';
import HolidayUtils from './HolidayUtils';

export const numWorkdays = (workingdays: IWorkingdays): number => {
  let num = 0;
  if (workingdays.monday) num++;
  if (workingdays.tuesday) num++;
  if (workingdays.wednesday) num++;
  if (workingdays.thursday) num++;
  if (workingdays.friday) num++;
  if (workingdays.saturday) num++;
  if (workingdays.sunday) num++;
  return num;
};

export const checkWorkday = (day: Dayjs, workingdays: IWorkingdays): boolean => {
  const dayOfWeek = day.day(); // 0-6, represents Sunday-Saturday

  if (
    (dayOfWeek === 1 && workingdays.monday) ||
    (dayOfWeek === 2 && workingdays.tuesday) ||
    (dayOfWeek === 3 && workingdays.wednesday) ||
    (dayOfWeek === 4 && workingdays.thursday) ||
    (dayOfWeek === 5 && workingdays.friday) ||
    (dayOfWeek === 6 && workingdays.saturday) ||
    (dayOfWeek === 0 && workingdays.sunday)
  ) {
    return true;
  }

  return false;
};

export const minutesFromTime = (time: string): number => {
  const array = time.split(':');
  return parseInt(array[0], 10) * 60 + parseInt(array[1], 10);
};

export const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const hourString = hours.toString().padStart(2, '0');
  const minutesString = remainingMinutes.toString().padStart(2, '0');
  return `${hourString}:${minutesString}`;
};

export const negativeMinutesToTime = (min: number): string => {
  if (min.toString().includes('-')) {
    const m = min * -1;
    return '-' + minutesToTime(m);
  } else {
    return minutesToTime(min);
  }
};

/* Unixtimestamp GMT */
export const dateStringToTimestamp = (date: string): number => {
  return dayjs(date).unix();
};

export const dateStringToWeek = (date: string): number => {
  dayjs.extend(weekOfYear);
  return dayjs(date)
    .locale({ ...locale })
    .week();
};

export const dateToWeek = (date: dayjs.Dayjs): number => {
  dayjs.extend(weekOfYear);
  return date.week();
};

export const dateStringToMonthYear = (date: string): string => {
  return dayjs(date)
    .locale({ ...locale })
    .format('MMMM  YYYY');
};

export const defaultTime = (day: Dayjs, profile: IProfile): ITime => {
  const time: ITime = {} as ITime;
  time.day = day.format('YYYY-MM-DD');
  time.notes = '';

  if (HolidayUtils.checkIsWorkday(profile.workingdays, day, profile.state)) {
    time.workingTime = profile.workingtime;
    time.availableTime = profile.availabletime;
    time.workday = true;
  } else {
    time.workingTime = 0;
    time.availableTime = 0;
    time.workday = false;
  }

  return time;
};

export const calculateSums = (times: ITime[]): ITimeDistribution => {
  let wT = 0;
  let aT = 0;

  times.forEach((time) => {
    if (!Number.isNaN(time.workingTime)) wT += time.workingTime;
    if (!Number.isNaN(time.availableTime)) aT += time.availableTime;
  });

  return { workingTime: wT, availableTime: aT };
};

export const filterByTimestampRange = (from: number, to: number, timeArray: ITime[]): ITime[] => {
  return timeArray.filter((time) => time.timestamp >= from && time.timestamp <= to);
};

export const sumOfThisYear = (result: ITime[]): ITimeDistribution => {
  const startYear = DayjsUtils.startOfCurrentYear().unix();
  const endToday = DayjsUtils.todayEnd().unix();

  const dataOfThisYear = filterByTimestampRange(startYear, endToday, result);
  return calculateSums(dataOfThisYear);
};

export const sumOfCurrentMonth = (result: ITime[]): ITimeDistribution => {
  const startMonth = DayjsUtils.firstDayOfCurrentMonth().unix();
  const endToday = DayjsUtils.todayEnd().unix();

  const dataOfCurrentMonth = filterByTimestampRange(startMonth, endToday, result);
  return calculateSums(dataOfCurrentMonth);
};

export const sumOfLastMonth = (result: ITime[]): ITimeDistribution => {
  const startOfLastMonath = DayjsUtils.startOfLastMonth().unix();
  const endOfLastMonth = DayjsUtils.endOfLastMonth().unix();

  const dataOfCurrentMonth = filterByTimestampRange(startOfLastMonath, endOfLastMonth, result);
  return calculateSums(dataOfCurrentMonth);
};

export const findfirstDay = (result: ITime[]): dayjs.Dayjs => {
  if (result.length === 0) {
    return dayjs();
  }

  const smallestTimestamp = Math.min(...result.map((time) => time.timestamp));
  return dayjs.unix(smallestTimestamp);
};

export const findTimeByTimestamp = (result: ITime[], timestamp: number): ITime | undefined => {
  return result.find((time) => time.timestamp === timestamp);
};

export const getDefaultTimeRange = () => {
  const from = dayjs('2020-01-01')
    .locale({ ...locale })
    .startOf('day')
    .unix();
  const today = dayjs()
    .locale({ ...locale })
    .endOf('day')
    .unix();
  return { from: from, to: today };
};

const workDaysTillTodayInMonthByStartDate = (startDay: dayjs.Dayjs, profile: IProfile): number => {
  const days: Dayjs[] = [];
  const today = DayjsUtils.todayStart();
  const d = Math.ceil(today.diff(startDay, 'day', true));
  for (let i = 0; i < d; i++) {
    const nextDay = startDay.add(i, 'day').locale({ ...locale });
    if (HolidayUtils.checkIsWorkday(profile.workingdays, nextDay, profile.state)) {
      days.push(nextDay);
    }
  }
  return days.length;
};

const workDaysInMonth = (fromTS: number, toTS: number, profile: IProfile): number => {
  const from = dayjs.unix(fromTS);
  const to = dayjs.unix(toTS);
  let count = 0;
  const d = Math.ceil(to.diff(from, 'day', true));
  for (let i = 0; i < d; i++) {
    const nextDay = from.add(i, 'day');
    if (HolidayUtils.checkIsWorkday(profile.workingdays, nextDay, profile.state)) {
      count++;
    }
  }
  return count;
};

export default {
  numWorkdays,
  checkWorkday,
  minutesFromTime,
  minutesToTime,
  dateStringToTimestamp,
  defaultTime,
  negativeMinutesToTime,
  dateStringToWeek,
  dateStringToMonthYear,
  calculateSums,
  filterByTimestampRange,
  sumOfThisYear,
  sumOfCurrentMonth,
  sumOfLastMonth,
  findfirstDay,
  findTimeByTimestamp,
  getDefaultTimeRange,
  workDaysTillTodayInMonthByStartDate,
  workDaysInMonth,
  dateToWeek,
};
